import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { api, day } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import { StaffAgentAccess } from './StaffAgentAccess'

export type Token = { id: string; client_name: string; approved_at: string | null; last_used_at: string | null; staff: boolean }

/** Agent access: make a token for your own coding agent, see each one's last use, revoke it. Partners and staff.
 *  CHANGE-070: /staff/agent shows the same tokens in the staff app's kit; /partner/agent is unchanged. */
export function AgentAccessPage() {
  const { pathname } = useLocation()
  return pathname.startsWith('/staff') ? <StaffAgentAccess /> : <PartnerAgentAccess />
}

/** Tokens: list, make one (shown once), revoke. Both pages use this. */
export function useAgentTokens() {
  const qc = useQueryClient()
  const key = ['agent', 'tokens']
  const list = useQuery({ queryKey: key, queryFn: () => api<{ rows: Token[] }>('GET', '/api/agent/grants') })
  const make = useMutation({
    mutationFn: (name: string) => api<{ token: string }>('POST', '/api/agent/tokens', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api('POST', `/api/agent/grants/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })
  return { list, make, revoke }
}

function PartnerAgentAccess() {
  const qc = useQueryClient()
  const key = ['agent', 'tokens']
  const list = useQuery({ queryKey: key, queryFn: () => api<{ rows: Token[] }>('GET', '/api/agent/grants') })
  const [name, setName] = useState('')
  const [fresh, setFresh] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const make = useMutation({
    mutationFn: () => api<{ token: string }>('POST', '/api/agent/tokens', { name }),
    onSuccess: (r) => {
      setFresh(r.token)
      setCopied(false)
      setName('')
      qc.invalidateQueries({ queryKey: key })
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api('POST', `/api/agent/grants/${id}/revoke`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  if (list.isLoading || !list.data) return list.error ? <ErrorBox error={list.error} /> : <Loading />
  const rows = list.data.rows
  const origin = window.location.origin

  return (
    <div className="stack" data-testid="agent-access">
      <div className="stack" style={{ gap: '0.5rem' }}>
        <h1>Agent access</h1>
      </div>

      <section className="panel stack">
        <h2>New token</h2>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) make.mutate()
          }}
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, like “Claude Code”" maxLength={60} aria-label="Name" data-testid="agent-token-name" />
          <button className="primary" type="submit" disabled={make.isPending || !name.trim()} data-testid="agent-token-new">
            New token
          </button>
        </form>
        {make.error ? <ErrorBox error={make.error} /> : null}
        {fresh ? (
          <div className="stack" data-testid="agent-token-fresh">
            <p style={{ margin: 0 }}>Copy it now. You won’t see it again.</p>
            <div className="prompt-box">
              <code data-testid="agent-token-value">{fresh}</code>
              <button type="button" className="primary" onClick={() => navigator.clipboard?.writeText(fresh).then(() => setCopied(true), () => {})}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              Tell your agent: “Read {origin}/agent.md. My token is above.” Or save it in <span className="mono">~/.config/ernd/token.json</span>.
            </p>
          </div>
        ) : null}
        <p className="muted" style={{ margin: 0 }}>
          No token yet? Tell your agent to read {origin}/agent.md. It will give you a link to approve.
        </p>
      </section>

      <section className="panel stack">
        <h2>Your tokens</h2>
        {rows.length === 0 ? (
          <p className="muted empty">None yet.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Made</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} data-testid="agent-token-row">
                    <td>{t.client_name}</td>
                    <td>{day(t.approved_at)}</td>
                    <td>{t.last_used_at ? day(t.last_used_at) : 'Never'}</td>
                    <td>
                      <button type="button" disabled={revoke.isPending} onClick={() => revoke.mutate(t.id)} data-testid="agent-token-revoke">
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {revoke.error ? <ErrorBox error={revoke.error} /> : null}
      </section>
    </div>
  )
}
